import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook otimizado para persistir estado no localStorage
 * Implementa debounce e throttle para evitar travamentos
 * 
 * @param {string} key - Chave única para identificar o valor no localStorage
 * @param {any} defaultValue - Valor padrão caso não exista no localStorage
 * @param {Object} options - Opções de configuração
 * @param {number} options.debounceMs - Delay antes de salvar (padrão: 300ms)
 * @param {number} options.throttleMs - Frequência mínima entre salvamentos (padrão: 100ms)
 * @param {boolean} options.useIdleCallback - Usar requestIdleCallback se disponível (padrão: true)
 * @returns {[any, function]} - Estado e função para atualizar
 */
export function usePersistedState(key, defaultValue, options = {}) {
  const {
    debounceMs = 300,
    throttleMs = 100,
    useIdleCallback = true,
  } = options;

  // Estado em memória
  const [state, setState] = useState(() => {
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null) {
        return JSON.parse(storedValue);
      }
      return defaultValue;
    } catch (error) {
      console.warn(`Erro ao recuperar ${key} do localStorage:`, error);
      return defaultValue;
    }
  });

  // Refs para controlar timers e throttle
  const saveTimeoutRef = useRef(null);
  const lastSaveRef = useRef(Date.now());
  const pendingSaveRef = useRef(false);

  // Função de salvamento otimizada
  const saveToStorage = useCallback(
    (value) => {
      const save = () => {
        try {
          localStorage.setItem(key, JSON.stringify(value));
          lastSaveRef.current = Date.now();
          pendingSaveRef.current = false;
        } catch (error) {
          console.warn(`Erro ao salvar ${key} no localStorage:`, error);
          // Em caso de quota excedida, tentar limpar dados antigos
          if (error.name === "QuotaExceededError") {
            console.warn("localStorage quota excedida. Considere limpar dados antigos.");
          }
        }
      };

      // Usar requestIdleCallback se disponível e configurado
      if (useIdleCallback && typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(save, { timeout: 1000 });
      } else {
        save();
      }
    },
    [key, useIdleCallback]
  );

  // Effect com debounce e throttle
  useEffect(() => {
    // Limpar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    pendingSaveRef.current = true;

    // Aplicar debounce
    saveTimeoutRef.current = setTimeout(() => {
      const timeSinceLastSave = Date.now() - lastSaveRef.current;

      // Aplicar throttle
      if (timeSinceLastSave >= throttleMs) {
        saveToStorage(state);
      } else {
        // Reagendar para respeitar throttle
        const delay = throttleMs - timeSinceLastSave;
        saveTimeoutRef.current = setTimeout(() => {
          if (pendingSaveRef.current) {
            saveToStorage(state);
          }
        }, delay);
      }
    }, debounceMs);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state, debounceMs, throttleMs, saveToStorage]);

  // Salvar imediatamente antes de desmontar ou fechar a página
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingSaveRef.current) {
        try {
          localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
          console.warn(`Erro ao salvar ${key} antes de desmontar:`, error);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Salvar ao desmontar o componente
      if (pendingSaveRef.current) {
        try {
          localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
          console.warn(`Erro ao salvar ${key} ao desmontar:`, error);
        }
      }
    };
  }, [key, state]);

  return [state, setState];
}

/**
 * Hook para gerenciar todas as preferências do usuário
 */
export function useUserPreferences() {
  // Tela atual (produtos, fornecedores, etc)
  const [currentScreen, setCurrentScreen] = usePersistedState(
    "vidal_current_screen",
    "produtos"
  );

  // Tabs dentro de cada tela
  const [screenTabs, setScreenTabs] = usePersistedState("vidal_screen_tabs", {
    produtos: "geral",
    fornecedores: "geral",
    transacoes: "geral",
  });

  // Tema (claro/escuro)
  const [theme, setTheme] = usePersistedState("vidal_theme", "dark");

  // Menu lateral aberto/fechado
  const [sidebarOpen, setSidebarOpen] = usePersistedState(
    "vidal_sidebar_open",
    true
  );

  // Função para atualizar a tab de uma tela específica
  const setScreenTab = (screen, tab) => {
    setScreenTabs((prev) => ({
      ...prev,
      [screen]: tab,
    }));
  };

  // Função para obter a tab atual de uma tela
  const getScreenTab = (screen) => {
    return screenTabs[screen] || "geral";
  };

  return {
    // Tela atual
    currentScreen,
    setCurrentScreen,

    // Tabs
    screenTabs,
    setScreenTab,
    getScreenTab,

    // Tema
    theme,
    setTheme,
    toggleTheme: () => setTheme((prev) => (prev === "dark" ? "light" : "dark")),

    // Sidebar
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar: () => setSidebarOpen((prev) => !prev),
  };
}

export default usePersistedState;
